import SiteCoreLinks from "@/components/SiteCoreLinks";
// /translate/naati/online — NAATI Online Worldwide Hub (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_COUNTRIES } from "@/data/naati-countries";
import { naatiOnlineHubSectionsTh } from "@/data/naati-online-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiDoctypeMatrix from "@/components/NaatiDoctypeMatrix";
import { naatiOfferCatalog, naatiServiceSchema, naatiSpeakableSchema, naatiHowToSchema, naatiPersonGraph } from "@/lib/naati-schema";
import { Globe2, MessageCircle, Phone, Plane, MapPin } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/naati/online/")({
  head: () => {
    const url = `${SITE}/translate/naati/online`;
    const title = "NAATI Online ทั่วโลก — บริการแปลรับรอง NAATI ออนไลน์ ส่งทุกประเทศ | NYC";
    const desc = "บริการแปล NAATI ออนไลน์ 100% ส่ง DHL Express ไป 50+ ประเทศทั่วโลก โดยนักแปล NAATI Professional /หน้า เสร็จ 2-5 วัน — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/online", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/online", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/online", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati/online", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/translate/naati/online", "/en/translate/naati/online")],
      scripts: [
        naatiSpeakableSchema(url),
        naatiOfferCatalog({ url, inLanguage: "th" }),
        naatiServiceSchema({ url, name: title, description: desc, inLanguage: "th" }),
        naatiHowToSchema({
          url,
          name: "ขั้นตอนสั่งแปล NAATI Online ส่งทั่วโลก",
          inLanguage: "th",
          steps: [
            { name: "ส่งสแกนเอกสาร", text: "สแกน 300dpi+ หรือถ่ายภาพชัด ส่งทาง LINE @nycli แจ้งประเทศปลายทาง" },
            { name: "ยืนยันราคา + ที่อยู่จัดส่ง", text: "ทีมเสนอราคาภายใน 30 นาที พร้อมประเทศ DHL Express ปลายทาง" },
            { name: "แปล + ประทับตรา NAATI", text: "นักแปล NAATI Certified ประทับ CPN ใน 2-4 วันทำการ" },
            { name: "จัดส่ง DHL Express ทั่วโลก", text: "ส่งฉบับจริง 3-6 วัน พร้อม PDF ทางอีเมล รับเลข tracking" },
          ],
          estimatedCostThb: 800,
        }),
        naatiPersonGraph(),
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "NAATI Online Worldwide Translation",
              serviceType: "NAATI Certified Translation with Global Shipping",
              areaServed: { "@type": "Place", name: "Worldwide" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 800, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: NaatiOnlineHubThPage,
});

function NaatiOnlineHubThPage() {
  const sections = naatiOnlineHubSectionsTh();
  const byRegion = NAATI_COUNTRIES.reduce<Record<string, typeof NAATI_COUNTRIES>>((acc, c) => {
    (acc[c.region] ||= [] as typeof NAATI_COUNTRIES).push(c);
    return acc;
  }, {});
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/naati" className="hover:text-gold">NAATI</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Online ทั่วโลก</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> NAATI Online · ส่งทั่วโลก
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            NAATI Online ทั่วโลก — แปลรับรอง NAATI ส่ง DHL Express 50+ ประเทศ
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg" data-speakable>
            สั่งแปล NAATI ออนไลน์ 100% ส่งสแกนทาง LINE @nycli ทีมแปลพร้อมรับรองโดย NAATI Certified Professional Translator เสร็จ 2-5 วัน ส่งฉบับจริงพร้อม PDF ทั่วโลก
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <PoliceSections sections={sections} />

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            เลือกประเทศปลายทาง (50+ ประเทศ)
          </h2>
          {Object.entries(byRegion).map(([region, countries]) => (
            <div key={region} className="mt-6">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-amber-700">{region}</h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {countries.map((c) => (
                  <Link
                    key={c.slug}
                    to="/translate/naati/online/$country"
                    params={{ country: c.slug }}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50"
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                    <span className="font-semibold text-slate-800">{c.nameTh}</span>
                    <span className="ml-auto text-[10px] text-slate-500">{c.shippingDays}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>

        <div className="mt-14"><ContactCTA /></div>
      </div>

      <NaatiDoctypeMatrix lang="th" />
      <NaatiAuthorByline lang="th" />

      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    </main>
  );
}
